import { _, Backbone, $, Radio, moment, Datepicker } from "../../../../vendor";
import InboomaBaseView from "../../InboomaBaseView";
import InboomaBaseCollectionView from "../../InboomaBaseCollectionView";
import { Dependencies, Biscuit, Notification, FormatHelper } from "../../../utils";
import Inboomai18n from "../../../utils/Inboomai18n";
import ct from "countries-and-timezones";
import Contingents from "../../../collections/Contingents";
import Locations from "../../../collections/Locations";

import CheckinOutTpl from "../../../templates/orders/CheckinOut/CheckinOutTpl.html";
import CheckinOutContingentItemTpl from "../../../templates/orders/CheckinOut/CheckinOutContingentItemTpl.html";
import Resources from "../../../collections/Resources";
import { getClientTariffs } from "../../../App";

// const select2 = require('select2')();

/**
 * @file orders/CheckinOut/index.js
 * @author Felix Albroscheit
 *
 * Step 1 - CHECKIN: Displays a two-column view that relates reservation_contingents
 * with their actual resources (which can be filtered by name or ID)
 * via /checkins endpoint
 *
 * Step 2 - CHECKOUT: Displays all resources of an already checked in reservation.
 * Those can be enriched with comments or flagged as "defect" to finally
 * become "checked out".
 *
 */

const defaultEmptyResource = {
  comment_checkin: "",
  comment_checkout: "",
};

let allUsedCartResourcesById = {};

const singleContingentView = InboomaBaseView.extend({
  template: CheckinOutContingentItemTpl,
  className() {
    // @todo Make background-color #e9ffe9 when selectionIsComplete
    return "inb-c-item inb-c-item--contingent inb-c-item--checkinout row toggled";
  },

  modelEvents: {
    changedCartData: "render",
    performSearch: "_performSearch",
  },

  templateContext() {
    return {
      _,
      moment,
      Biscuit,
      locations: Radio.channel("locations").request("get:data"),
      isCartView: this.getOption("isCartView"),
      isStepCheckout: this.getOption("isStepCheckout"),
      searchQuery: this._searchQuery,
      selectedResources: this.model.get("_checkin").selectedResources,
      selectionIsComplete:
        this.model.get("_checkin").total <= _.size(this.model.get("_checkin").selectedResources),
      checkoutComplete: this.getOption("checkoutComplete"),
      date: this._date,
      location_checkout: this._locationCheckout,
    };
  },

  ui: {
    checkoutDateField: 'input[name="checkout-date"]',
    checkoutTimeField: 'input[name="checkout-time"]',
    locationSelectField: "#inb-checkout-location",
  },

  events: {
    "click .inb-resource__checkinout_action": "_checkinOutAction",
    "click .smart-resource-select": "_smartResourceSelect",
    "click .single-checkin": "_checkinSingleResource",
    "click .single-checkout": "_checkoutSingleResource",
    "keyup input.checkin-comment": "_updateCheckinComment",
    "keyup input.checkout-comment": "_updateCheckoutComment",
    "change @ui.checkoutTimeField": "_changeCheckoutTime",
    "change @ui.locationSelectField": "_changeCheckoutLocation",
  },

  _searchQuery: "",

  _smartResourceSelect() {
    // Auto-checkin the required amount of resources

    for (
      let i = _.size(this.model.attributes._checkin.selectedResources);
      i < this.model.attributes._checkin.total;
      i++
    ) {
      const resource = this.model.get("resources").models[i];

      if (typeof resource !== "undefined") {
        if (
          typeof this.model.attributes._checkin.selectedResources[resource.get("id")] == "undefined"
        ) {
          this.model.attributes._checkin.selectedResources[resource.get("id")] =
            _.clone(defaultEmptyResource);
        }
        // @todo else -> while
      }
    }

    this.model.trigger("changedCartData");
  },

  _updateCheckinComment(ev) {
    const div = $(ev.currentTarget).closest(".inb-resource"),
      resource_id = div.attr("data-inb-resource-id");

    this.model.attributes._checkin.selectedResources[resource_id]["comment_checkin"] = div
      .find("input.checkin-comment")
      .val();
  },

  _updateCheckoutComment(ev) {
    const div = $(ev.currentTarget).closest(".inb-resource"),
      resource_id = div.attr("data-inb-resource-id");

    this.model.attributes._checkin.selectedResources[resource_id]["comment_checkout"] = div
      .find("input.checkout-comment")
      .val();
  },

  _changeCheckoutLocation(ev) {
    this._locationCheckout = ev.target.value;
  },

  _changeCheckoutTime(ev) {
    const values = ev.target.value.split(":");

    if (values.length != 2) return;

    var date = this._date;
    const hours = ev.target.value.split(":")[0];
    const minutes = ev.target.value.split(":")[1];

    if (Number(hours) === NaN) return;
    if (Number(minutes) === NaN) return;

    date.set("hours", hours);
    date.set("minutes", minutes);
    this._date = moment(date);

    this.render();
  },

  _checkinSingleResource(ev) {
    // to-do: what should be done here? (also show link in CheckinOutContingentItemTpl.html again if finally implemented)
    const that = this,
      div = $(ev.currentTarget).closest(".inb-resource"),
      resource_id = div.attr("data-inb-resource-id");

    this.getOption("checkinSingleResource")(
      this.model.attributes._checkin.selectedResources[resource_id],
      resource_id,
      function () {
        that.render();
      },
    );

    //alert('NOT YET IMPLEMENTED');

    return false;
  },

  _checkoutSingleResource(ev) {
    const that = this,
      div = $(ev.currentTarget).closest(".inb-resource"),
      resource_id = div.attr("data-inb-resource-id");

    this.getOption("checkoutSingleResource")(
      this.model.attributes._checkin.selectedResources[resource_id],
      resource_id,
      function () {
        that.model.attributes._checkin.selectedResources[resource_id]["is_checked_out"] = true;
        that.render();
      },
    );

    return false;
  },

  _checkinOutAction(ev) {
    const div = this.$(ev.currentTarget).closest(".inb-resource"),
      resource_id = div.attr("data-inb-resource-id");

    if (this.getOption("isCartView")) {
      // Remove resource from cart
      delete this.model.attributes._checkin.selectedResources[resource_id];
    } else {
      // Add resource to cart
      this.model.attributes._checkin.selectedResources[resource_id] = _.clone(defaultEmptyResource);
    }

    this.model.trigger("changedCartData");
  },

  _performSearch(val) {
    // We do not want to rely on Obscura.Collection or Marionette.View.filter to filter our collection as
    // the view is too simple for that -> therefore we fake a search-filter in onRender

    this._searchQuery = val.toString().toLowerCase();
    this.render();
  },

  initialize() {
    // Save resources by key for later access by parent views
    _.each(this.model.get("resources").models, function (resource) {
      allUsedCartResourcesById[resource.get("id")] = resource;
    });
    const res = Object.keys(this.model.attributes._checkin.selectedResources);
    const checkout =
      res.length > 0 ? this.model.attributes._checkin.selectedResources[res[0]].checkout : "";
    const country = ct.getCountry(Biscuit.get("client.country"));
    this._date = this.getOption("checkoutComplete") ? moment(checkout) : moment();
    const loc_checkout =
      res.length > 0
        ? this.model.attributes._checkin.selectedResources[res[0]].location_checkout
        : "";
    // preselect location checkin if resource has been checked in but not checked out yet
    this._locationCheckout =
      loc_checkout === null
        ? this.model.attributes._checkin.selectedResources[res[0]].location_checkin
        : loc_checkout;
  },

  onRender() {
    // Only allow rendering when...
    // ...right-hand (cart) view: Resources have been selected
    // ...left-hand (all conts) view: No search filter or valid filter was applied

    let allowRender = false;

    const val = this._searchQuery;

    if (
      this.getOption("isCartView") &&
      _.size(this.model.attributes._checkin.selectedResources) >= 1
    ) {
      allowRender = true;
    }

    if (!this.getOption("isCartView")) {
      if (val === "") {
        allowRender = true;
      } else if (this.model.get("name").toLowerCase().indexOf(val) > -1) {
        allowRender = true;
      } else {
        // Changes made here should be also done in tpl
        _.each(this.model.get("resources").models, function (resource) {
          if (resource.get("name").toLowerCase().indexOf(val) > -1) allowRender = true;

          if (resource.get("mac")) {
            if (resource.get("mac").toLowerCase().indexOf(val) > -1) allowRender = true;
          }

          if (resource.get("external_resource_id")) {
            if (resource.get("external_resource_id").toLowerCase().indexOf(val) > -1)
              allowRender = true;
          }
        });
      }
    }

    const date = this._date,
      that = this,
      datepicker = new Datepicker({
        field: this.getUI("checkoutDateField")[0],
        i18n: Inboomai18n.getPikadayI18n(),
        format: "D. MMMM YYYY",
        firstDay: 1, // monday
        //minDate : new Date(),
        onSelect() {
          const newDate = this.getMoment().format("YYYY-MM-DD").split("-");
          date
            .set("year", Number(newDate[0]))
            .set("month", Number(newDate[1]) - 1)
            .set("date", Number(newDate[2]));

          that._date = moment(date);
        },
        onClose() {
          that.render();
        },
      });

    if (allowRender) {
      this.$el.removeClass("hide");
    } else {
      this.$el.addClass("hide");
    }
  },
});

const AllContingentsListView = InboomaBaseCollectionView.extend({
  childView: singleContingentView,
  el: "#inb-checkin-all-contingents .content",
  template: _.noop,

  childViewOptions: {
    isCartView: false,
  },

  initialize() {
    this.render();
  },

  onDomRefresh: _.noop,
});

const CartContingentsListView = InboomaBaseCollectionView.extend({
  childView: singleContingentView,
  el: "#inb-checkin-cart .content",
  template: _.noop,

  childViewOptions() {
    return {
      isCartView: true,
      isStepCheckout: this.getOption("isStepCheckout"),
      checkinSingleResource: this.getOption("checkinSingleResource"),
      checkoutSingleResource: this.getOption("checkoutSingleResource"),
      checkoutComplete: this.getOption("checkoutComplete"),
    };
  },

  collectionEvents: {
    all: "_toggleEmptyListPlaceholder",
  },

  initialize() {
    this.render();
  },

  _toggleEmptyListPlaceholder() {
    // Called after each children's view render

    if (this.$(".inb-c-item-details-row").length == 0) {
      this.$("#inb-checkin-empty-list").removeClass("hide");
    } else {
      this.$("#inb-checkin-empty-list").addClass("hide");
    }
  },

  onRender() {
    if (!this.getOption("isStepCheckout")) {
      this.$el.append(
        `
                <div id="inb-checkin-empty-list" class="text-center">
                <br />
                <img src="/assets/img/select_list.svg" style="max-width:280px;" />
                <br /><br />
                <p>` +
          this.t("checkin_add_elements_to_cart") +
          `</p>
                </div>`,
      );
    }
  },

  onDomRefresh: _.noop,
});

export default InboomaBaseView.extend({
  template: CheckinOutTpl,
  el: "#inb-c-main-orders",

  templateContext() {
    return {
      Biscuit,
      locations: this._locations,
      checkinDate: this._isStepCheckout ? moment(this.model.get("checkins")[0]["checkin"]) : null,
      checkoutComplete:
        this._isStepCheckout &&
        _.every(
          this.model.get("checkins"),
          (checkin) =>
            typeof checkin.location_checkout !== "undefined" && checkin.location_checkout !== null,
        ),
      begin: moment(this.model.get("begin")),
      end: moment(this.model.get("end")),
      reservation: this.model.toJSON(),
    };
  },

  ui: {
    searchField: "#inb-checkin-all-contingents__search",
    submitBtn: "#inb-checkin__submit",
    checkoutDateField: 'input[name="checkout-date"]',
    checkoutTimeField: 'input[name="checkout-time"]',
    orderCancelLink: "#inb-checkout-cancel-link",
    orderCheckoutBtn: "#submit-order-checkout",
  },

  events: {
    "keyup @ui.searchField": "_searchAction",
    "click @ui.submitBtn": "_submit",
    "change @ui.checkoutDateField": "getCheckoutPrice",
    "change @ui.checkoutTimeField": "getCheckoutPrice",
    "click @ui.orderCancelLink": "_cancelOrder",
    "click @ui.orderCheckoutBtn": "_submitCheckout",
  },

  _searchAction() {
    const val = this.getUI("searchField").val();

    _.each(this.collection.models, function (model) {
      model.trigger("performSearch", val);
    });
  },

  // After click on right hand side topbar submit button:
  _submit() {
    if (this._isStepCheckout) {
      this._submitCheckout();
    } else {
      this._submitCheckin();
    }
  },

  _submitCheckin() {
    const that = this,
      totalSubmittableResources = this.$("#inb-checkin-cart .inb-c-item-details-row").length;

    if (!totalSubmittableResources) {
      alert(this.t("checkin_submit_error_empty"));
      return;
    }

    // @todo if totalSubmittableResources < all checkins.total ...

    this.model.set("checkins", []);

    const submitModel = new (Backbone.Model.extend({
      url: "/checkins",
    }))();

    submitModel.set({
      checkin: moment().format("YYYY-MM-DD HH:mm:ss"),
      reservation_id: this.model.get("id"),
    });

    let count = 0;

    const checkIfBulkCheckinFinished = function () {
      if (++count == totalSubmittableResources) {
        new Notification({
          message: that.t("checkin_submit_success"),
          duration: 5000,
          closeable: true,
          icon: "fi fi-check",
        });

        if (that.model.get("status") !== "inprogress") {
          that.model.save(
            {
              status: "inprogress",
            },
            {
              type: "PATCH",
              patch: true,
            },
          );
        }

        Backbone.history.navigate("orders", { trigger: true });
      }
    };

    _.each(this.collection.models, function (model) {
      _.each(model.get("_checkin").selectedResources, function (meta, resource_id) {
        const fullResource = allUsedCartResourcesById[Number(resource_id)];

        that.model.attributes.checkins = [];

        submitModel.save(
          {
            resource_id: Number(resource_id),
            location_checkin: that._activeLocation.get("id"),
            comment_checkin: meta.comment_checkin,
          },
          {
            success(__, data) {
              that.model.attributes.checkins.push(data.data);
              checkIfBulkCheckinFinished();
            },
            error(__, response) {
              response = response.responseJSON.data;

              if (typeof response.code !== "undefined" && response.code === 422) {
                // code 422: Checkin was already done on resource (= active booking elsewhere)
                // Remove checked in resource from both lists (mark as booked)

                alert(
                  that.t("checkin_error_resource_${a}_unavailable", {
                    a:
                      fullResource.get("name") +
                      (fullResource.get("external_resource_id")
                        ? " (" +
                          that.t("external_resource_id_short") +
                          ": " +
                          fullResource.get("external_resource_id") +
                          ")"
                        : ""),
                  }),
                );
              }
            },
          },
        );
      });
    });
  },

  checkIfBulkCheckoutFinished() {
    let totalSubmittableResources = 0,
      submittedResources = 0;

    _.each(this.collection.models, function (model) {
      _.each(model.get("_checkin").selectedResources, function (checkin, resource_id) {
        totalSubmittableResources++;
        if (checkin.is_checked_out) {
          submittedResources++;
        }
      });
    });

    if (!totalSubmittableResources) {
      alert(this.t("checkin_submit_error_empty"));
      return;
    }

    if (submittedResources === totalSubmittableResources) {
      new Notification({
        message: this.t("checkout_submit_success"),
        duration: 5000,
        closeable: true,
        icon: "fi fi-check",
      });

      if (this.model.get("status") !== "confirmed") {
        this.model.save(
          {
            status: "completed",
          },
          {
            type: "PATCH",
            patch: true,
            /*
                        Redirect to orders page disabled as requested in #21523, 14
                        success () {
                            Backbone.history.navigate('orders', { trigger:true });
                        },
                        error () {
                            Backbone.history.navigate('orders', { trigger:true });
                        }*/
          },
        );
      }
      setTimeout(() => {
        document.location.reload();
      }, 1000);

      //this.render();      // update view to status "everything checked out"
    }
  },

  _submitCheckout() {
    // Same as in _submitCheckin, but we send a PATCH to /checkins/[ID]

    if (document.getElementById("inb-checkout-location").value == "") {
      document.getElementById("inb-checkout-location-alert").classList.remove("hide");
      alert(this.t("no_location_selected") + " " + this.t("select_checkout_location_info"));
      return;
    }

    const that = this;

    _.each(this.collection.models, function (model) {
      _.each(
        model.get("_checkin").selectedResources,
        function checkoutSingleResourceWrapper(checkin, resource_id) {
          if (!checkin.is_checked_out) {
            that.checkoutSingleResource(checkin, resource_id);
          }
        },
      );
    });
  },

  checkinSingleResource() {
    // @todo Enable single checkin (yet we only have bulk checkin _submitCheckin)
    // same as below in checkoutSingleResource
  },

  checkoutSingleResource(checkin, resource_id, onSuccess = _.noop, onError = _.noop) {
    const that = this;

    const submitModel = new (Backbone.Model.extend({
      url: "/checkins",
    }))();

    submitModel.url = "/checkins/" + checkin.checkin_id;

    // get selected location for checkout if client is only bikesharing account
    // set location coordinates of selected location or use coordinates set by app if no location has been selected
    let location = {};
    if (
      Biscuit.get("client.isBikeSharing") === "true" &&
      Biscuit.get("client.hasMultipleResourcetypes") === "false"
    ) {
      // assume that there is only one contingent
      location.id = document.getElementById("inb-checkout-location").value;
      if (location.id == "") {
        document.getElementById("inb-checkout-location-alert").classList.remove("hide");
        alert(this.t("no_location_selected") + " " + this.t("select_checkout_location_info"));
        return;
      } else {
        for (var i = 0; i < this._locations.length; i++) {
          if (this._locations[i].get("id").toString() == location.id) {
            location.latitude = this._locations[i].get("latitude");
            location.longitude = this._locations[i].get("longitude");
            break;
          }
        }
      }
    } else {
      ((location.id = this._activeLocation.get("id")),
        (location.latitude = this._activeLocation.get("latitude")));
      location.longitude = this._activeLocation.get("longitude");
    }

    submitModel.save(
      {
        id: checkin.checkin_id,
        checkout: document.getElementById("inb-hidden-checkout-date").value,
        location_checkout: Number(location.id),
        resource_id: Number(resource_id),
        //lat: location.latitude,
        //lng: location.longitude,
        comment_checkout: checkin.comment_checkout,
      },
      {
        type: "PATCH",
        success(__, data) {
          const _checkin = data.data.checkins;

          // Patch reservation checkin item
          let actualReservationCheckin = _.find(
            that.model.attributes.checkins,
            (_checkin) => _checkin.id == checkin.checkin_id,
          );
          actualReservationCheckin = [data.data.checkins];

          // Patch local checkin with updated checkout data
          checkin.is_checked_out = true;
          checkin.modified = _checkin.modified;
          checkin.modified_by = _checkin.modified_by;
          checkin.location_checkout = _checkin.location_checkout;

          onSuccess(data);

          new Notification({
            message: that.t("checkout_single_resource_done") + " (ID: " + resource_id + ")",
            duration: 5000,
            type: "success",
            closeable: true,
            icon: "fi fi-mail",
          });

          that.checkIfBulkCheckoutFinished();

          var btn = document.getElementsByClassName("button-checkin-abort")[0];
          $(btn).trigger("click");
        },
        error(__, response) {
          onError();

          response = response.responseJSON.data;
          if (response.code == 404) {
            //try to bypass the payment
            let bestaetigung = window.confirm(
              'Folgender Fall ist eingetreten: "' +
                that.t(response.message) +
                '". Wir weisen darauf hin, dass in diesem Fall beim Check-Out der nachzuzahlende Betrag nicht beim Nutzer eingezogen wird. Soll der Check-Out dennoch durchgeführt werden?',
            );
            if (bestaetigung) {
              submitModel.save(
                {
                  id: checkin.checkin_id,
                  checkout: document.getElementById("inb-hidden-checkout-date").value,
                  location_checkout: Number(location.id),
                  resource_id: Number(resource_id),
                  //lat: location.latitude,
                  //lng: location.longitude,
                  comment_checkout: checkin.comment_checkout,
                  bypassPayment: true,
                },
                {
                  type: "PATCH",
                  success(__, data) {
                    const _checkin = data.data.checkins;

                    // Patch reservation checkin item
                    let actualReservationCheckin = _.find(
                      that.model.attributes.checkins,
                      (_checkin) => _checkin.id == checkin.checkin_id,
                    );
                    actualReservationCheckin = [data.data.checkins];

                    // Patch local checkin with updated checkout data
                    checkin.is_checked_out = true;
                    checkin.modified = _checkin.modified;
                    checkin.modified_by = _checkin.modified_by;
                    checkin.location_checkout = _checkin.location_checkout;

                    onSuccess(data);

                    new Notification({
                      message:
                        that.t("checkout_single_resource_done") + " (ID: " + resource_id + ")",
                      duration: 5000,
                      type: "success",
                      closeable: true,
                      icon: "fi fi-mail",
                    });

                    that.checkIfBulkCheckoutFinished();

                    var btn = document.getElementsByClassName("button-checkin-abort")[0];
                    $(btn).trigger("click");
                  },
                  error(__, response) {
                    onError();

                    response = response.responseJSON.data;
                    // e.g. when checkout was already done
                    alert("Error: " + response.message);
                    Backbone.history.navigate("orders", { trigger: true });
                  },
                },
              );
            } else if (response.code == 403) {
              //try to bypass the payment
              let bestaetigung = window.confirm(
                "Ein Fehler ist beim Versuch die Zahlung einzuziehen aufgetreten. Wir weisen darauf hin, dass in diesem Fall beim Check-Out der nachzuzahlende Betrag nicht beim Nutzer eingezogen wird. Soll der Check-Out dennoch durchgeführt werden?",
              );
              if (bestaetigung) {
                submitModel.save(
                  {
                    id: checkin.checkin_id,
                    checkout: document.getElementById("inb-hidden-checkout-date").value,
                    location_checkout: Number(location.id),
                    resource_id: Number(resource_id),
                    //lat: location.latitude,
                    //lng: location.longitude,
                    comment_checkout: checkin.comment_checkout,
                    bypassPayment: true,
                  },
                  {
                    type: "PATCH",
                    success(__, data) {
                      const _checkin = data.data.checkins;

                      // Patch reservation checkin item
                      let actualReservationCheckin = _.find(
                        that.model.attributes.checkins,
                        (_checkin) => _checkin.id == checkin.checkin_id,
                      );
                      actualReservationCheckin = [data.data.checkins];

                      // Patch local checkin with updated checkout data
                      checkin.is_checked_out = true;
                      checkin.modified = _checkin.modified;
                      checkin.modified_by = _checkin.modified_by;
                      checkin.location_checkout = _checkin.location_checkout;

                      onSuccess(data);

                      new Notification({
                        message:
                          that.t("checkout_single_resource_done") + " (ID: " + resource_id + ")",
                        duration: 5000,
                        type: "success",
                        closeable: true,
                        icon: "fi fi-mail",
                      });

                      that.checkIfBulkCheckoutFinished();

                      var btn = document.getElementsByClassName("button-checkin-abort")[0];
                      $(btn).trigger("click");
                    },
                    error(__, response) {
                      onError();

                      response = response.responseJSON.data;
                      // e.g. when checkout was already done
                      alert("Error: " + response.message);
                      Backbone.history.navigate("orders", { trigger: true });
                    },
                  },
                );
              }
            } else {
              Backbone.history.navigate("orders", { trigger: true });
            }
          }
        },
      },
    );
  },

  initialize() {
    console.log("checkinout- reservation model beforehand", this.model.toJSON());

    this._isStepCheckout = true;

    if (
      typeof this.model.get("checkins") === "undefined" ||
      this.model.get("checkins").length === 0
    ) {
      this._isStepCheckout = false;
    }

    const that = this,
      app = Radio.channel("app");

    $("html,body").scrollTop(0);

    app.trigger("collection:fetching");

    new Dependencies({
      contingents: new Contingents(),
      locations: new Locations(),
    }).then(() => {
      that._init();
      app.trigger("collection:loaded");
    });
  },

  /**
   * Requests and sets all dependencies given in reservation (contingents; resources; locations).
   * Once done, calls this.render()
   */
  _init() {
    const that = this,
      locations = Radio.channel("locations").request("get:data"),
      contingents = Radio.channel("contingents").request("get:data");

    // recursive function, called periodically until view finished render process
    const requestPrice = () => {
      setTimeout(() => {
        if (document.getElementById("inb-hidden-checkout-date")) {
          that.getCheckoutPrice();
        } else {
          requestPrice();
        }
      }, 300);
    };
    requestPrice();

    this.collection = new Backbone.Collection();

    let resourceIdsByContingent = {};

    if (this._isStepCheckout) {
      _.each(this.model.get("checkins"), function (checkin) {
        // Map reservation.checkins into resourceIdsByContingent

        if (typeof resourceIdsByContingent[checkin.resource.contingent_id] === "undefined") {
          resourceIdsByContingent[checkin.resource.contingent_id] = {};
        }

        const isCheckedOut = typeof checkin.checkout !== "undefined" && checkin.checkout !== null;

        resourceIdsByContingent[checkin.resource.contingent_id][checkin.resource.id] =
          _.clone(defaultEmptyResource);
        resourceIdsByContingent[checkin.resource.contingent_id][checkin.resource.id]["created"] =
          checkin.created;
        resourceIdsByContingent[checkin.resource.contingent_id][checkin.resource.id]["created_by"] =
          checkin.created_by;
        resourceIdsByContingent[checkin.resource.contingent_id][checkin.resource.id]["modified"] =
          checkin.modified;
        resourceIdsByContingent[checkin.resource.contingent_id][checkin.resource.id][
          "modified_by"
        ] = checkin.modified_by;
        resourceIdsByContingent[checkin.resource.contingent_id][checkin.resource.id][
          "location_checkin"
        ] = checkin.location_checkin;
        resourceIdsByContingent[checkin.resource.contingent_id][checkin.resource.id][
          "location_checkout"
        ] = checkin.location_checkout;
        resourceIdsByContingent[checkin.resource.contingent_id][checkin.resource.id][
          "comment_checkin"
        ] = checkin.comment_checkin;
        resourceIdsByContingent[checkin.resource.contingent_id][checkin.resource.id]["checkin_id"] =
          checkin.id;
        resourceIdsByContingent[checkin.resource.contingent_id][checkin.resource.id]["checkout"] =
          checkin.checkout;
        // If checkout was already done for this resource, show it as disabled
        resourceIdsByContingent[checkin.resource.contingent_id][checkin.resource.id][
          "is_checked_out"
        ] = isCheckedOut;
        resourceIdsByContingent[checkin.resource.contingent_id][checkin.resource.id][
          "comment_checkout"
        ] = isCheckedOut ? checkin.comment_checkout : "";

        resourceIdsByContingent[checkin.resource.contingent_id][checkin.resource.id]["longitude"] =
          checkin.resource.longitude; // value usualy set by app
        resourceIdsByContingent[checkin.resource.contingent_id][checkin.resource.id]["latitude"] =
          checkin.resource.latitude; // value usualy set by app
      });
    }

    // Get all order contingents with their total and (if is checkout) selected amount
    let requiredContingentsWithAmount = {};

    _.each(this.model.get("contingents_reservations"), (contingent) => {
      if (typeof requiredContingentsWithAmount[contingent.contingent_id] === "undefined") {
        requiredContingentsWithAmount[contingent.contingent_id] = {
          id: contingent.contingent_id,
          total: contingent.amount,
          selectedResources: {},
        };

        if (typeof resourceIdsByContingent[contingent.contingent_id] !== "undefined") {
          // Is checkout step -> overwrite resource data in order to show
          // previous (checkin-)comment and checkin-id (required for checkout endpoint call)
          _.each(
            resourceIdsByContingent[contingent.contingent_id],
            function (resource, resource_id) {
              requiredContingentsWithAmount[contingent.contingent_id].selectedResources[
                resource_id
              ] = resource;
            },
          );
        }
      } else {
        requiredContingentsWithAmount[contingent.contingent_id]["total"] += contingent.amount;
      }
    });

    // Set selectable locations and default location for location input field
    // @todo Use values from reservation.begin_location / .end_location
    let managerHasLocations = Biscuit.get("user.user_locations");

    if (managerHasLocations !== "") {
      managerHasLocations = managerHasLocations.split(",");
    }

    this._locations = []; // Locations available to logged in user; not required anymore.

    _.each(locations.models, (location) => {
      if (
        managerHasLocations.length &&
        !_.contains(managerHasLocations, location.get("id").toString())
      ) {
        return;
      }

      that._locations.push(location);
    });

    if (this._locations.length === 0) {
      alert("Can not check-in/-out: You have no locations to select from.");
      Backbone.history.navigate("orders", { trigger: true });
      return;
    }

    locations.fetchById(
      this.model.get(this._isStepCheckout ? "end_location" : "begin_location"),
      (location) => {
        that._activeLocation = location;
      },
    );

    // Fetch all contingents from endpoint into local collection
    let count = 0,
      size = _.size(requiredContingentsWithAmount);

    _.each(requiredContingentsWithAmount, (val, id) => {
      contingents.fetchById(Number(id), (contingent) => {
        // Set internal contingent type with selected- and amount-state
        contingent.set("_checkin", val);

        // Only add available resources to contingent
        const contingentHasResources = new Resources();
        contingentHasResources.url += `?contingent_id=${id}`;
        contingentHasResources._pagination.limit = 999;

        if (!that._isStepCheckout) {
          contingentHasResources.url += "&find=!CheckedIn";
        }

        contingentHasResources.fetch({
          success(availableResources, response) {
            // availableResources -> initialized, but empty models o_O

            contingent.get("resources").reset(response.data);
            that.collection.add(contingent);

            if (++count === size) {
              that.render();
            }
          },
        });
      });
    });
  },

  getCheckoutPrice() {
    // get date and time values
    /*
        let checkoutDate = moment(document.getElementsByName('checkout-date')[0].value);
        let checkoutTime = document.getElementsByName('checkout-time')[0].value.split(':');
        checkoutDate.set('hours',checkoutTime[0]);
        checkoutDate.set('minutes',checkoutTime[1]);
        */
    let _checkoutTime = document.getElementById("inb-hidden-checkout-date").value;
    console.log("cont res.: ", this.model.attributes);
    const RequestHelper = Backbone.Model.extend({ urlRoot: "/prices/getPrice" });
    const requestHelper = new RequestHelper({
      Products: {},
      begin: this.model.get("begin") + (this.model.get("begin").length < 19 ? ":00" : ""),
      //'end' : checkoutDate.format('YYYY-MM-DD HH:mm:ss'),
      end: _checkoutTime,
      contingent_id: Number(this.model.attributes.checkins[0].resource.contingent_id),
      count_of_contingents: 1,
      pricetariff_id: this.model.attributes.contingents_reservations[0].pricetariff_id,
      user_id: Biscuit.get("user.id"),
      voucher_id: this.model.attributes.contingents_reservations[0].voucher_id,
      for_checkout_extended: true,
      order_id: this.model.attributes.order_id,
    });

    const that = this;

    return new Promise((resolve, reject) => {
      requestHelper.save(null, {
        type: "POST",
        headers: {
          "Accept-Language": Biscuit.get("user.lang").replace("-", "_"),
        },
        success(model, response) {
          var clientTariffs = getClientTariffs();
          for (var i = 0; i < clientTariffs.length; i++) {
            if (
              clientTariffs[i].id ==
              that.model.attributes.contingents_reservations[0].pricetariff_id
            ) {
              // proof of extension settings produce further costs
              if (clientTariffs[i].extension == "extend_charge") {
                if (moment(that.model.attributes.end).isBefore(_checkoutTime)) {
                  document.getElementById("inb-checkout-extension-hint").classList.remove("hide");
                  document
                    .getElementById("inb-checkout-extra-value")
                    .parentNode.classList.remove("hide");
                  let extraCost =
                    typeof that.model.get("price") !== "undefined"
                      ? Number(response.price.price) - Number(that.model.get("price"))
                      : null;
                  console.log("extra-cost: ", extraCost);
                  document.getElementById("inb-checkout-extra-value").innerHTML =
                    extraCost !== null
                      ? FormatHelper.currencyFormat(extraCost) +
                        " " +
                        Biscuit.get("client.currency")
                      : "";
                } else {
                  document.getElementById("inb-checkout-extension-hint").classList.add("hide");
                }
              }
            }
          }

          const el = document.getElementById("inb-checkout-price-value");
          if (typeof el !== undefined && el !== null)
            el.innerHTML =
              FormatHelper.currencyFormat(response.price.price) +
              " " +
              Biscuit.get("client.currency");
          resolve();
        },
        error(model, response) {
          console.log("Error, could not request order.");
          reject();
        },
      });
    });
  },

  _cancelOrder() {
    const RequestHelper = Backbone.Model.extend({
      urlRoot: "/orders/" + this.model.get("order_id"),
    });
    const that = this;

    const requestHelper = new RequestHelper({
      status: "cancelled",
    });

    return new Promise((resolve, reject) => {
      requestHelper.save(null, {
        type: "PATCH",
        success(model, response) {
          new Notification({
            message: that.t("order_cancelled_success"),
            duration: 5000,
            type: "success",
            closeable: true,
            icon: "fi fi-mail",
          });
          var btn = document.getElementsByClassName("button-checkin-abort")[0];
          $(btn).trigger("click");
          resolve();
        },
        error(model, response) {
          console.log("Error, could not request order.");
          reject();
        },
      });
    });
  },

  onRender() {
    if (!this._isStepCheckout) {
      this.allContingentsListView = new AllContingentsListView({
        collection: this.collection,
      });

      // For "barcode scanner"
      this.getUI("searchField").focus();
    }

    this.cartContingentsListView = new CartContingentsListView({
      collection: this.collection,
      isStepCheckout: this._isStepCheckout,
      checkinSingleResource: this.checkinSingleResource.bind(this),
      checkoutSingleResource: this.checkoutSingleResource.bind(this),
      checkoutComplete:
        this._isStepCheckout &&
        _.every(
          this.model.get("checkins"),
          (checkin) =>
            typeof checkin.location_checkout !== "undefined" && checkin.location_checkout !== null,
        ),
    });

    /* Removed location selector as requested in #21523, 3.
        const that = this,
              locations = Radio.channel('locations').request('get:data');

        this.getUI('locationSelectField').select2({
            placeholder: this.t('checkin_out_location')
        }).on('select2:select', function (ev) {

            const data = ev.params.data,
                  location_id = data.id;

            that._activeLocation = locations.get(location_id);

        });*/

    $(".inb-next-page-button").hide();
  },
});
